import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useAuth } from "../../hooks/useAuth";
import { useChat } from "../../hooks/useChat";
import { useChatContext } from "../../contexts/ChatContext";
import { FiMessageCircle, FiX, FiChevronDown } from "react-icons/fi";
import ChatWindow from "./ChatWindow";
import MessageInput from "./MessageInput";
import ChatLoginForm from "./ChatLoginForm";
import UpgradePrompt from "./UpgradePrompt";
import { Message } from "../../types";
import { stripeAPI } from "../../api";
import { FaPaw } from "react-icons/fa";
import { translations, SupportedLang, languageNames  } from "../../api/translations";
import { PlanType } from "../../types/index";
import AuthForm from "./AuthForm"; // adjust path if different

const LanguageButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  margin-right: auto; /* push to left */
`;

// 👇 ye styled component add karo

const LanguageDropdown = styled.div`
  position: absolute;
  top: 100%; /* button ke neeche khule */
  right: 0;
  background: white;
  color: black;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  padding: 8px 0;
  z-index: 20000;
  min-width: 140px;

  button {
    background: none;
    border: none;
    padding: 8px 12px;
    width: 100%;
    text-align: left;
    cursor: pointer;
    font-size: 14px;
    &:hover {
      background: #f3f4f6;
    }
  }
`;

const WidgetContainer = styled.div`
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 10000;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const FloatingButton = styled.button<{ $isOpen: boolean }>`
  width: 80px;
  height: 80px;
  border: 2px solid #fff;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  filter: none;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
  }

  svg {
    width: 100%;
    height: 100%;
    transition: all 0.3s ease;
  }

  &:hover svg {
    filter: brightness(1.1) drop-shadow(0 2px 8px rgba(255, 153, 0, 0.3));
  }
`;

const ChatContainer = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 400px;
  max-width: 95vw;
  height: 600px;
  max-height: 90vh;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  display: ${(props) => (props.$isOpen ? "flex" : "none")};
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e5e7eb;

  @media (max-width: 600px) {
    width: 100vw;
    height: 100vh;
    bottom: 0;
    right: 0;
    border-radius: 0;
  }
`;

const ChatHeader = styled.div`
  background: #e89b3c;
  color: white;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
`;

const ChatTitle = styled.div`
  display: flex;
  flex-direction: column;
`;

const ChatSubtitle = styled.div`
  font-size: 12px;
  opacity: 0.8;
  font-weight: normal;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ChatContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const UsageBar = styled.div`
  padding: 8px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-size: 12px;
  color: #6b7280;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const UsageText = styled.span`
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  color: #dc2626;
  padding: 12px 16px;
  font-size: 12px;
  border-bottom: 1px solid #fecaca;
  line-height: 1.4;

  .error-suggestion {
    color: #991b1b;
    font-style: italic;
    margin-top: 4px;
    font-size: 11px;
  }
`;

const DemoModeBanner = styled.div`
  background: #fef3c7;
  color: #92400e;
  padding: 8px 16px;
  font-size: 12px;
  border-bottom: 1px solid #fde68a;
  text-align: center;
  font-weight: 500;
`;

const DemoLimitBanner = styled.div`
  background: #fef3c7;
  color: #92400e;
  padding: 8px 16px;
  font-size: 12px;
  border-bottom: 1px solid #fde68a;
  text-align: center;
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DemoLimitText = styled.span`
  font-weight: 500;
`;

const ChatWidget: React.FC = () => {
  // 1) Auth state
  const {
    user,
    usage,
    limits,
    error: authError,
    setError,
    refreshUser,
    updateUsage,
  } = useAuth();

  // 2) Language state
  const [language, setLanguage] = useState<SupportedLang>("en");
  const [showLangDropdown, setShowLangDropdown] = useState(false);
const [showSignup, setShowSignup] = useState(false);

  // 3) Chat state (depends on user, limits, language)
  const {
    messages,
    loading,
    error: chatError,
    sendMessage,
    sendVoiceMessage,
    sendImageMessage,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    isRecording,
    isPaused,
    recordingTime,
    guestId,
    guestUsage,
    clearError: clearChatError,
  } = useChat({
    user,
    refreshUser,
    updateUsage,
    limits: limits || undefined,
    language,
  });

  // 4) UI state
  const [isOpen, setIsOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [postAuthAction, setPostAuthAction] = useState<{
    plan: "free" | "free-trial" | "basic" | "premium";
  } | null>(null);
const [showSignupModal, setShowSignupModal] = useState(false);




  // Load from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("chatLang") as SupportedLang | null;
    if (saved) setLanguage(saved);
  }, []);

  // Save when language changes
  useEffect(() => {
    localStorage.setItem("chatLang", language);
  }, [language]);

  // Translation helper
  const t = (key: string) => translations[language][key] || key;

  // Clear errors when component mounts or user changes
  useEffect(() => {
    setError(null);
  }, [user?.id, setError]);

  // Filter out authentication errors that should only be shown in login context
  const isAuthError =
    authError &&
    (authError.includes("Password must be at least") ||
      authError.includes("Authentication failed") ||
      authError.includes("Sign in failed") ||
      authError.includes("Registration failed") ||
      authError.includes("Invalid email or password") ||
      authError.includes("No account found") ||
      authError.includes("Incorrect password") ||
      authError.includes("Please enter") ||
      authError.includes("Validation failed") ||
      authError.includes("Request failed with status code 400") ||
      authError.includes("Request failed with status code 401"));

  const currentError = isAuthError ? chatError : authError || chatError;

  // Auto-clear errors after 10 seconds
  useEffect(() => {
    if (currentError) {
      const timer = setTimeout(() => {
        setError(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [currentError, setError]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Enable auto-scroll to last message when chat opens or messages change
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [isOpen, messages]);

  // Check if user has reached limits after login (for guest-to-user conversion)
  useEffect(() => {
    try {
      if (user && usage && limits && !showUpgrade) {
        // For free plan, check combined limit (text + voice + image)
        if (user.plan_type === "free") {
          const totalUsage =
            (Number(usage?.text_messages_sent) || 0) +
            (Number(usage?.voice_minutes_used) || 0) +
            (Number(usage?.image_uploads) || 0);
          const freeLimit =
            limits && limits.text_messages !== -1 ? limits.text_messages : 6; // fallback to 6 if not set
          if (totalUsage > freeLimit) {
            console.log("Free plan limit reached:", {
              totalUsage,
              freeLimit,
              usage,
            });
            setShowUpgrade(true);
            return;
          }
        } else {
          // For paid plans, check individual limits
          const hasReachedTextLimit =
            limits?.text_messages !== -1 &&
            (Number(usage?.text_messages_sent) || 0) > limits.text_messages;
          if (hasReachedTextLimit) {
            console.log("Paid plan text limit reached:", {
              textMessages: usage?.text_messages_sent,
              limit: limits?.text_messages,
            });
            setShowUpgrade(true);
            return;
          }
        }
      }
    } catch (error) {
      console.error("Error in usage limit check:", error);
    }
  }, [user, usage, limits, showUpgrade]);

  useEffect(() => {
    if (user && usage && limits) {
      console.log("DEBUG: user usage/limits after signin or refresh:", {
        usage,
        limits,
        planType: user.plan_type,
      });
    }
  }, [user, usage, limits]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    // Clear any errors when opening the widget
    if (!isOpen) {
      setError(null);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setShowUpgrade(false);
    // Clear any errors when closing the widget
    setError(null);
  };

const handleSendMessage = async (message: string) => {
  // 🚀 GUEST CASE
  if (!user) {
    if (guestUsage && guestUsage.messagesRemaining <= 0) {
      // ✅ Agar limit already 0 hai → direct modal
      setShowUpgrade(true);
      return;
    }

    try {
      // Message send
      await sendMessage({
        message,
        guest_id: guestId,
        language,
        user_type: "guest",
      });

      // ✅ Usage update turant
      if (guestUsage) {
        guestUsage.messagesUsed += 1;
        guestUsage.messagesRemaining -= 1;
      }

      // ✅ Check again after increment
      if (guestUsage && guestUsage.messagesRemaining <= 0) {
        setShowUpgrade(true);
      }
    } catch (err: any) {
      console.error("❌ Guest message failed:", err);
    }
  }

  // 🚀 LOGGED-IN CASE
  else {
    if (usage && limits) {
      // Free plan combined limit
      if (user.plan_type === "free") {
        const totalUsage =
          (Number(usage.text_messages_sent) || 0) +
          (Number(usage.voice_minutes_used) || 0) +
          (Number(usage.image_uploads) || 0);

        const freeLimit =
          limits.text_messages !== -1 ? limits.text_messages : 6;

        if (totalUsage >= freeLimit) {
          // ✅ block immediately
          setShowUpgrade(true);
          return;
        }
      }
    }

    try {
      await sendMessage({ message, language });

      // ✅ Increment usage
      if (usage && user.plan_type === "free") {
        usage.text_messages_sent =
          (Number(usage.text_messages_sent) || 0) + 1;
      }

      // ✅ After increment → check again
if (user.plan_type === "free" && limits) {
  if (!usage) return; // usage null hai to skip

  const totalUsage =
    (Number(usage.text_messages_sent) || 0) +
    (Number(usage.voice_minutes_used) || 0) +
    (Number(usage.image_uploads) || 0);

  if (totalUsage >= limits.text_messages) {
    setShowUpgrade(true);
  }
}

    } catch (err: any) {
      console.error("❌ User message failed:", err);
    }
  }
};



  const handleUpgrade = async (plan: PlanType) => {
    if (!user) {
      setPostAuthAction({ plan });
      setShowUpgrade(false);
    } else {
      try {
        setUpgradeLoading(true);
        const response = await stripeAPI.createCheckoutSession(plan);
        if (response.url) {
          window.location.href = response.url;
        }
      } catch (err: any) {
        console.error("Failed to create Stripe checkout session:", err);
        setError(
          err.response?.data?.message || "Could not connect to payment gateway."
        );
      } finally {
        setUpgradeLoading(false);
      }
    }
  };

  const handleAuthSuccess = () => {
    console.log("ChatWidget - handleAuthSuccess called");
    setShowUpgrade(false);
    console.log("ChatWidget - Calling loadHistory...");

    // If there was a pending upgrade action, trigger it now
    if (postAuthAction) {
      handleUpgrade(postAuthAction.plan);
      setPostAuthAction(null);
    }
    // The useEffect will handle checking if user has reached limits after login
  };

  const getUsageText = () => {
    try {
      if (!user) {
        // Guest user - show guest usage with dynamic limits from backend
        if (guestUsage) {
          // For guest users, we should use the limits from the backend response
          // The guestUsage.messagesUsed is already calculated with the correct limit
          // We need to get the total limit from the backend response
          const totalLimit =
            guestUsage.messagesUsed + guestUsage.messagesRemaining;
          return `${guestUsage.messagesUsed}/${totalLimit} messages`;
        }
        // If no guestUsage yet, don't show anything until we get the dynamic limits
        return "";
      }


      if (!usage || !limits) return "";

      if (limits?.text_messages === -1) {
        return "Unlimited messages";
      }

      // Count all message types: text + voice + image
      const totalMessagesUsed =
        (Number(usage.text_messages_sent) || 0) +
        (Number(usage.voice_minutes_used) || 0) +
        (Number(usage.image_uploads) || 0);

      return `${totalMessagesUsed}/${limits?.text_messages || 0} messages`;
    } catch (error) {
      console.error("Error in getUsageText:", error);
      return "Loading...";
    }
  };

  const getVoiceUsageText = () => {
    try {
      if (!user) {
        // Guest user - use dynamic limits from backend
        if (guestUsage && typeof guestUsage.messagesUsed === "number") {
          // For guest users, we should use the limits from the backend response
          // Since voice messages count towards the same limit as text messages
          const totalLimit =
            guestUsage.messagesUsed + guestUsage.messagesRemaining;
          return `${guestUsage.messagesUsed}/${totalLimit} voice messages`;
        }
        // If no guestUsage yet, don't show anything until we get the dynamic limits
        return "";
      }
      if (!usage || !limits) return "";
// After increment
if (user?.plan_type === "free") {
  const totalUsage =
    (Number(usage?.text_messages_sent) || 0) +
    (Number(usage?.voice_minutes_used) || 0) +
    (Number(usage?.image_uploads) || 0);

  const freeLimit =
    limits && limits.text_messages !== -1 ? limits.text_messages : 6;

  if (totalUsage >= freeLimit) {
    setShowUpgrade(true);
  }
} else if (!user && guestUsage && guestUsage.messagesRemaining <= 0) {
  setShowUpgrade(true);
}

      if (user.plan_type === "basic") {
        return `${usage.voice_minutes_used || 0}/${
          limits.voice_minutes
        } voice messages`;
      }

      return "";
    } catch (error) {
      console.error("Error in getVoiceUsageText:", error);
      return "Loading...";
    }
  };

  // Error boundary for the entire widget
  if (currentError) {
    console.error("ChatWidget error:", currentError);
  }

  return (
    <WidgetContainer>
      {!isOpen && (
        <FloatingButton
          $isOpen={isOpen}
          onClick={handleToggle}
          data-testid="chat-widget-button"
        >
          <svg
            width="80"
            height="80"
            viewBox="0 0 240 240"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g
              transform="translate(0,240) scale(0.1,-0.1)"
              fill="#E89B3C"
              stroke="none"
            >
              <path
                d="M1090 2354 c-167 -14 -394 -89 -500 -164 -15 -11 -31 -20 -34 -20
-13 0 -112 -78 -167 -131 -189 -183 -306 -408 -344 -664 -19 -127 -19 -210 0
-333 58 -369 276 -676 605 -849 307 -163 667 -175 990 -35 l45 20 61 -34 c180
-100 367 -126 554 -78 36 9 66 18 68 19 2 2 -67 85 -152 184 -86 100 -156 186
-156 191 0 5 20 36 44 67 24 32 64 96 89 143 264 493 130 1110 -315 1448 -145
110 -323 190 -493 220 -70 13 -230 21 -295 16z m310 -130 c252 -57 461 -194
619 -404 51 -68 124 -207 146 -280 47 -155 48 -163 48 -330 0 -139 -4 -178
-22 -245 -51 -185 -127 -327 -245 -460 -20 -22 -36 -42 -35 -45 0 -3 56 -70
124 -150 69 -79 125 -148 125 -152 0 -14 -116 -9 -182 8 -222 56 -463 253
-677 556 l-53 75 -57 -15 c-90 -25 -270 -21 -356 7 -173 57 -272 146 -311 279
-9 29 -13 64 -10 77 6 25 51 52 126 75 142 45 225 110 249 193 41 144 59 171
141 215 73 40 154 56 275 55 215 -1 382 -60 470 -167 106 -130 143 -378 78
-528 -23 -53 -85 -83 -156 -74 -29 3 -88 9 -132 12 -204 17 -282 117 -226 290
37 110 43 154 31 206 -11 45 -56 108 -77 108 -5 0 -7 -26 -5 -57 3 -48 -3 -79
-37 -180 -23 -69 -41 -140 -41 -165 0 -134 94 -258 219 -290 20 -5 94 -15 165
-23 195 -21 269 -2 331 84 50 68 60 116 60 281 0 152 -14 220 -66 322 -36 70
-146 171 -229 210 -276 130 -637 105 -802 -55 -50 -48 -71 -87 -89 -162 -24
-102 -33 -109 -228 -186 -147 -57 -185 -112 -161 -231 27 -132 105 -242 223
-315 82 -51 98 -58 187 -84 79 -23 250 -31 340 -17 l45 8 55 -77 c30 -42 112
-135 183 -207 70 -72 127 -134 127 -137 0 -10 -131 -48 -221 -64 -209 -37
-413 -7 -614 90 -307 148 -502 399 -576 740 -18 79 -17 324 0 395 51 213 133
369 271 514 60 63 172 156 188 156 6 0 12 3 14 8 10 24 208 108 313 133 129
30 327 31 455 3z"
              />
              <path
                d="M987 1412 c-20 -22 -23 -122 -5 -140 17 -17 43 -15 59 4 20 25 17
108 -5 133 -23 26 -28 26 -49 3z"
              />
            </g>
          </svg>
        </FloatingButton>
      )}
      <ChatContainer
        $isOpen={isOpen}
        data-testid="chat-widget"
        className={isOpen ? "open" : ""}
      >
        {/* Always show ChatHeader */}
        <ChatHeader className="flex items-center justify-between px-4 py-2 bg-[#1a1a1a]">
          {/* Left side: Title & Subtitle */}
          <ChatTitle className="flex flex-col">
            <span className="flex items-center text-white text-lg font-semibold">
              {t("title")}
            </span>
            <ChatSubtitle className="text-white text-sm font-normal">
              {t("subtitle")}
            </ChatSubtitle>
          </ChatTitle>

          {/* Right side: Buttons */}
          <div className="flex items-center gap-3">
            <div className="relative flex flex-col items-center">
              {/* Language Button */}
{/* Language Button */}
<LanguageButton
  onClick={() => setShowLangDropdown(!showLangDropdown)}
  className="text-white flex items-center gap-1"
>
  {languageNames[language as SupportedLang]} <FiChevronDown />
</LanguageButton>

{/* Label under the button */}
<span className="block text-center text-sm mt-1 text-white font-normal">
  {translations[language as SupportedLang]?.selectLanguage || "Select Language"}
</span>

{/* Dropdown */}
{showLangDropdown && (
  <LanguageDropdown className="absolute top-full mt-2 right-0 bg-white shadow-md rounded-md z-50">
    {Object.keys(translations).map((lang) => (
      <button
        key={lang}
        onClick={() => {
          setLanguage(lang as SupportedLang);
          setShowLangDropdown(false);
        }}
        className={`block px-3 py-1 text-sm w-full text-left ${
          language === lang ? "bg-gray-200 font-semibold" : "text-black hover:bg-gray-100"
        }`}
      >
        {languageNames[lang as SupportedLang]}
      </button>
    ))}
  </LanguageDropdown>
)}


            </div>

            {/* Close Button */}
            <CloseButton
              onClick={handleClose}
              className="text-white text-xl font-bold hover:text-gray-300"
            >
              ×
            </CloseButton>
          </div>
        </ChatHeader>

        {/* Show global error for all users */}
        {currentError && (
          <ErrorMessage>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div style={{ flex: 1 }}>
                {/* Always render as string */}
  {Array.isArray(currentError)
    ? currentError.map((err: any, i: number) => (
        <div key={i}>{err.msg || JSON.stringify(err)}</div>
      ))
    : typeof currentError === "object"
    ? (currentError as any).msg || JSON.stringify(currentError)
    : String(currentError)}

  {/* suggestions same as before */}
  {typeof currentError === "string" &&
    currentError.includes("AI service is taking longer than expected to respond") && (
      <div className="error-suggestion">
        💡 Tip: Please try again in a moment
      </div>
  )}
                {typeof currentError === "string" &&
                  currentError.includes("Try uploading a smaller image") && (
                    <div className="error-suggestion">
                      💡 Tip: Try uploading a smaller image or check your
                      internet connection
                    </div>
                  )}
                {typeof currentError === "string" &&
                  currentError.includes(
                    "AI service is temporarily unavailable"
                  ) && (
                    <div className="error-suggestion">
                      💡 Tip: Please try again in a few minutes
                    </div>
                  )}
                {typeof currentError === "string" &&
                  currentError.includes("Failed to connect") && (
                    <div className="error-suggestion">
                      💡 Tip: Check your internet connection and try again
                    </div>
                  )}
                {typeof currentError === "string" &&
                  currentError.includes("File too large") && (
                    <div className="error-suggestion">
                      💡 Tip: Use an image smaller than 5MB
                    </div>
                  )}
                {typeof currentError === "string" &&
                  currentError.includes("Invalid file type") && (
                    <div className="error-suggestion">
                      💡 Tip: Use a supported image format (JPG, PNG, GIF, WebP)
                    </div>
                  )}
                {typeof currentError === "string" &&
                  currentError.includes(
                    "AI service is currently overloaded"
                  ) && (
                    <div className="error-suggestion">
                      💡 Tip: Please try again in a few minutes
                    </div>
                  )}
                {typeof currentError === "string" &&
                  currentError.includes("Unable to connect to AI service") && (
                    <div className="error-suggestion">
                      💡 Tip: Check your internet connection and try again
                    </div>
                  )}
              </div>
              <button
                onClick={() => {
                  setError(null); // Clear auth error
                  clearChatError(); // Clear chat error
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#dc2626",
                  cursor: "pointer",
                  padding: "4px",
                  marginLeft: "8px",
                  fontSize: "16px",
                }}
                title="Dismiss error"
              >
                ×
              </button>
            </div>
          </ErrorMessage>
        )}

       

        <ChatContent>
        {user ? (
  <>
    {/* ✅ Usage bar — for logged-in users */}
    {(getUsageText() || getVoiceUsageText()) && (
      <UsageBar>
        <UsageText>{getUsageText()}</UsageText>
      </UsageBar>
    )}

    {/* ✅ Upgrade prompt or normal chat */}
    {showSignup ? (
      <AuthForm
        onAuthSuccess={() => {
          setShowSignup(false);
          handleAuthSuccess();
        }}
        onClose={() => setShowSignup(false)}
        variant="widget"
      />
    ) : showUpgrade ? (
      <UpgradePrompt
        onUpgrade={handleUpgrade}
        onClose={() => {
          setShowUpgrade(false);
          setError(null);
        }}
        loading={upgradeLoading}
        currentPlan={user?.plan_type || "free"}
        limits={limits || undefined}
      />
    ) : (
      <ChatWindow
        messages={[
          {
            id: Date.now(),
            type: "text",
            userMessage: "",
            botResponse:
              "Hi,\nThis is Ryan Matthews, your dog-training K9 Coach AI expert. How can I help you?",
            timestamp: new Date().toISOString(),
          },
          ...messages,
        ]}
        loading={loading}
        messagesEndRef={messagesEndRef}
        t={t}
      />
    )}

    {/* ✅ Message input */}
    <MessageInput
      onSendMessage={handleSendMessage}
      onRecord={startRecording}
      onStopRecording={stopRecording}
      onPauseRecording={pauseRecording}
      onResumeRecording={resumeRecording}
      onCancelRecording={cancelRecording}
      onSendImage={(file, desc) => sendImageMessage(file, desc, language)}
      isRecording={isRecording}
      isPaused={isPaused}
      recordingTime={recordingTime}
      loading={loading}
      disabled={loading || showUpgrade}
      t={t}
    />
  </>
) : (
  <>
    {/* 🚫 Guest flow */}
   {(getUsageText() || getVoiceUsageText()) && (
  <UsageBar>
    <UsageText>{getUsageText()}</UsageText>

    {/* ✅ Show “Sign Up” when guest */}
    {!user && (
      <button
        onClick={() => setShowSignupModal(true)}
        className="ml-4 px-4 py-1.5 text-xs font-semibold text-white rounded-md bg-gradient-to-r from-[#E89B3C] to-[#FF9900] hover:opacity-90 transition"
      >
        SignUp/SignIn
      </button>
    )}
  </UsageBar>
)}

    {/* ✅ Auth / Upgrade / Chat for guests */}
    {showSignup ? (
      <AuthForm
        onAuthSuccess={() => {
          setShowSignup(false);
          handleAuthSuccess();
        }}
        onClose={() => setShowSignup(false)}
        variant="widget"
      />
    ) : postAuthAction ? (
      <ChatLoginForm
        onLoginSuccess={handleAuthSuccess}
        onClose={handleClose}
      />
    ) : showUpgrade ? (
      <ChatLoginForm
        onLoginSuccess={handleAuthSuccess}
        onClose={() => {
          setShowUpgrade(false);
          setError(null);
        }}
      />
    ) : (
      <>
        <ChatWindow
          messages={[
            {
              id: Date.now(),
              type: "text",
              userMessage: "",
              botResponse:
                "Hi,\nThis is Ryan Matthews, your dog-training K9 Coach AI exper can I help you?",
              timestamp: new Date().toISOString(),
            },
            ...messages,
          ]}
          loading={loading}
          messagesEndRef={messagesEndRef}
          t={t}
        />

        <MessageInput
          onSendMessage={handleSendMessage}
          onRecord={startRecording}
          onStopRecording={stopRecording}
          onPauseRecording={pauseRecording}
          onResumeRecording={resumeRecording}
          onCancelRecording={cancelRecording}
          onSendImage={(file, desc) => sendImageMessage(file, desc, language)}
          isRecording={isRecording}
          isPaused={isPaused}
          recordingTime={recordingTime}
          loading={loading}
          disabled={loading || showUpgrade}
          t={t}
        />
      </>
    )}
  </>
)}
{/* ✅ Global Sign Up Modal (same as App.tsx modal) */}
{showSignupModal && (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      background: "rgba(0,0,0,0.35)",
      zIndex: 20000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <AuthForm
      variant="modal" // 👈 exact same modal look as navbar
      onAuthSuccess={() => {
        setShowSignupModal(false);
        handleAuthSuccess();
      }}
      onClose={() => setShowSignupModal(false)}
    />
  </div>
)}


        </ChatContent>
      </ChatContainer>
    </WidgetContainer>
  );
};

export default ChatWidget;
